import React, { useState, useRef, useCallback } from 'react';
import './PullToRefresh.css';

const THRESHOLD = 80; // Pixels to pull before triggering refresh
const MAX_PULL = 120; // Maximum pull distance

function PullToRefresh({ onRefresh, children, disabled = false }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);

  const canPull = useCallback(() => {
    if (disabled || isRefreshing) return false;
    // Only allow pull when scrolled to top
    const scrollTop = containerRef.current?.scrollTop || window.scrollY;
    return scrollTop <= 0;
  }, [disabled, isRefreshing]);

  const handleTouchStart = useCallback((e) => {
    if (!canPull()) return;
    startY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [canPull]);

  const handleTouchMove = useCallback((e) => {
    if (!isPulling || !canPull()) return;

    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;

    if (diff > 0) {
      // Apply resistance to make it feel natural
      const resistance = Math.min(diff * 0.5, MAX_PULL);
      setPullDistance(resistance);

      // Prevent default scroll when pulling
      if (resistance > 10) {
        e.preventDefault();
      }
    }
  }, [isPulling, canPull]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;
    setIsPulling(false);

    if (pullDistance >= THRESHOLD && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(60); // Keep indicator visible while refreshing

      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, onRefresh]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const shouldTrigger = pullDistance >= THRESHOLD;

  return (
    <div
      ref={containerRef}
      className="pull-to-refresh"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className={`pull-indicator ${isRefreshing ? 'refreshing' : ''} ${shouldTrigger ? 'ready' : ''}`}
        style={{
          transform: `translateY(${pullDistance - 60}px)`,
          opacity: pullDistance > 10 ? 1 : 0
        }}
      >
        <div
          className="pull-indicator__spinner"
          style={{
            transform: `rotate(${progress * 360}deg)`,
          }}
        >
          <span className="material-icons" aria-hidden="true">
            {isRefreshing ? 'sync' : 'arrow_downward'}
          </span>
        </div>
        {!isRefreshing && pullDistance > 20 && (
          <span className="pull-indicator__text">
            {shouldTrigger ? 'Soltar para actualizar' : 'Desliza para actualizar'}
          </span>
        )}
      </div>

      {/* Content with transform */}
      <div
        className="pull-to-refresh__content"
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: isPulling ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default PullToRefresh;
